import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { Modal } from "../../components/Modal";
import { useAuth } from "../../auth/AuthContext";
import { accessControlClient } from "../../acessos/acessosClient";
import {
  CreateRoleRequest,
  RenameRoleRequest,
  SetRoleFeaturesRequest,
  type AccessControlVm,
  type AccessRoleDto,
} from "../../web-api-client";

// Gestão de Acessos: cargos geríveis, cada um com as funcionalidades (áreas do menu) que
// concede; um utilizador pode ter vários cargos e o acesso é a união. Página só de
// Administrators — o backend exige o cargo em todos os endpoints desta área.

const SYSTEM_ROLE_LABELS: Record<string, string> = {
  Administrator: "Administrador",
  User: "Utilizador",
  TeamMember: "Equipa interna",
};

export const roleDisplayName = (name: string): string =>
  SYSTEM_ROLE_LABELS[name] ?? name;

const parseErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message: unknown }).message ?? "");
    try {
      const parsed = JSON.parse(message);
      if (parsed && typeof parsed === "object") {
        const validation = parsed.errors
          ? Object.values(parsed.errors as Record<string, string[]>).flat()
          : [];
        if (validation.length > 0) return validation.join(" ");
        return parsed.detail || parsed.title || message;
      }
    } catch {
      // não é JSON — segue o texto tal como veio.
    }
    if (message) return message;
  }
  return "Não foi possível concluir a operação.";
};

export function GestaoAcessosView() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("Administrator") ?? false;

  const [vm, setVm] = useState<AccessControlVm | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [checkedFeatures, setCheckedFeatures] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newRoleName, setNewRoleName] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<AccessRoleDto | null>(null);

  const load = useCallback(async (keepSelection = true) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await accessControlClient.getAccessControl();
      setVm(result);
      setSelectedRoleId((previous) => {
        const roles = result.roles ?? [];
        if (keepSelection && previous && roles.some((role) => role.id === previous)) {
          return previous;
        }
        return roles[0]?.id ?? null;
      });
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const selectedRole = useMemo(
    () => vm?.roles?.find((role) => role.id === selectedRoleId) ?? null,
    [vm, selectedRoleId],
  );

  // Funcionalidades por ordem alfabética do nome — mais fácil de encontrar uma na matriz.
  const sortedFeatures = useMemo(
    () =>
      (vm?.features ?? [])
        .slice()
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-PT")),
    [vm?.features],
  );

  // A matriz reflete sempre o cargo selecionado tal como veio do servidor.
  useEffect(() => {
    setCheckedFeatures(new Set(selectedRole?.featureCodes ?? []));
    setRenameValue(selectedRole && !selectedRole.isSystem ? (selectedRole.name ?? "") : "");
    setNotice(null);
  }, [selectedRole]);

  const toggleFeature = (code: string) =>
    setCheckedFeatures((previous) => {
      const next = new Set(previous);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });

  // Todas marcadas → desmarca tudo; caso contrário marca tudo. Nada é gravado até "Guardar".
  const toggleAllFeatures = () =>
    setCheckedFeatures((previous) => {
      const all = (vm?.features ?? []).map((feature) => feature.code ?? "");
      return previous.size === all.length ? new Set() : new Set(all);
    });

  const saveFeatures = async () => {
    if (!selectedRole?.id || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await accessControlClient.setRoleFeatures(
        selectedRole.id,
        new SetRoleFeaturesRequest({ featureCodes: Array.from(checkedFeatures) }),
      );
      setNotice("Funcionalidades guardadas. As alterações aplicam-se no próximo início de sessão de cada utilizador.");
      await load();
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const createRole = async () => {
    const name = newRoleName.trim();
    if (name === "" || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      const roleId = await accessControlClient.createRole(new CreateRoleRequest({ name }));
      setNewRoleName("");
      await load(false);
      setSelectedRoleId(roleId);
      setNotice("Cargo criado. Escolha as funcionalidades e guarde.");
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const renameRole = async () => {
    if (!selectedRole?.id || selectedRole.isSystem || isSaving) return;
    const name = renameValue.trim();
    if (name === "" || name === selectedRole.name) return;
    setIsSaving(true);
    setError(null);
    try {
      await accessControlClient.renameRole(selectedRole.id, new RenameRoleRequest({ name }));
      await load();
      setNotice("Cargo renomeado.");
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRole = async () => {
    if (!confirmDelete?.id || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await accessControlClient.deleteRole(confirmDelete.id);
      setConfirmDelete(null);
      await load(false);
      setNotice("Cargo apagado. Os utilizadores ficaram com os cargos restantes.");
    } catch (err) {
      setError(parseErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader title="Gestão de Acessos" subtitle="Cargos e funcionalidades da aplicação." />
        <div className="p-6 text-sm text-secondary-text">
          Esta área é reservada a Administradores.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Gestão de Acessos"
        subtitle="Cada cargo concede funcionalidades; um utilizador pode ter vários cargos e o acesso é a soma de todos."
      />
      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
          {/* Sempre montado: uma live region só anuncia se já existir no DOM quando o texto muda. */}
          <div role="alert">
            {error && (
              <div
                className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
                style={{ borderRadius: "var(--radius)" }}
              >
                {error}
              </div>
            )}
          </div>

          <div role="status">
            {notice && (
              <div
                className="border border-border px-4 py-3 text-sm text-foreground"
                style={{ borderRadius: "var(--radius)", backgroundColor: "var(--accent)" }}
              >
                {notice}
              </div>
            )}
          </div>

          <p className="text-sm text-secondary-text">
            As alterações feitas aqui aplicam-se a cada utilizador{" "}
            <span className="text-foreground" style={{ fontWeight: "var(--font-weight-medium)" }}>
              no próximo início de sessão
            </span>
            . Desativar a conta em Utilizadores impede novos inícios de sessão dessa pessoa.
          </p>

          {isLoading && !vm ? (
            <p className="text-sm text-secondary-text">A carregar…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Cargos */}
              <div className="space-y-2">
                <h2
                  className="text-foreground"
                  style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}
                >
                  Cargos
                </h2>
                <div className="space-y-2">
                  {(vm?.roles ?? []).map((role) => {
                    const selected = role.id === selectedRoleId;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        aria-pressed={selected}
                        className="w-full text-left border border-border bg-card p-3"
                        style={{
                          borderRadius: "var(--radius)",
                          ...(selected
                            ? { borderColor: "var(--primary)", backgroundColor: "var(--accent)" }
                            : null),
                        }}
                        onClick={() => setSelectedRoleId(role.id ?? null)}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="flex-1 text-foreground"
                            style={{ fontWeight: "var(--font-weight-medium)" }}
                          >
                            {roleDisplayName(role.name ?? "")}
                          </span>
                          {role.isSystem && (
                            <span
                              className="text-xs px-2 py-0.5"
                              style={{
                                borderRadius: "var(--radius)",
                                backgroundColor: "var(--muted)",
                                color: "var(--muted-foreground)",
                              }}
                            >
                              Sistema
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-secondary-text mt-1">
                          {role.userCount === 1
                            ? "1 utilizador"
                            : `${role.userCount ?? 0} utilizadores`}
                          {" · "}
                          {role.canEditFeatures
                            ? `${role.featureCodes?.length ?? 0} funcionalidades`
                            : "acesso total"}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="border border-border bg-card p-3 space-y-2" style={{ borderRadius: "var(--radius)" }}>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-secondary-text">Criar cargo novo</span>
                    <input
                      type="text"
                      value={newRoleName}
                      placeholder="Ex.: Escritório"
                      onChange={(event) => setNewRoleName(event.target.value)}
                      className="px-3 py-2 border border-border rounded bg-background text-foreground"
                      style={{ borderRadius: "var(--radius)" }}
                    />
                  </label>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-primary-foreground disabled:opacity-50"
                    style={{
                      borderRadius: "var(--radius)",
                      backgroundColor: "var(--primary)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                    disabled={newRoleName.trim() === "" || isSaving}
                    onClick={createRole}
                  >
                    Criar cargo
                  </button>
                </div>
              </div>

              {/* Funcionalidades do cargo selecionado */}
              <div className="md:col-span-2 space-y-3">
                {selectedRole ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2
                        id="features-heading"
                        className="text-foreground flex-1"
                        style={{ fontSize: "var(--text-section-title)", fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        Funcionalidades de «{roleDisplayName(selectedRole.name ?? "")}»
                      </h2>
                      {!selectedRole.isSystem && (
                        <>
                          <input
                            type="text"
                            aria-label={`Renomear cargo ${roleDisplayName(selectedRole.name ?? "")}`}
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            className="px-3 py-2 border border-border rounded bg-background text-foreground text-sm"
                            style={{ borderRadius: "var(--radius)" }}
                          />
                          <button
                            type="button"
                            className="px-3 py-2 border border-border rounded text-foreground text-sm hover:bg-accent/30 disabled:opacity-50"
                            style={{ borderRadius: "var(--radius)" }}
                            disabled={isSaving || renameValue.trim() === "" || renameValue.trim() === selectedRole.name}
                            onClick={renameRole}
                          >
                            Renomear
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 border border-border rounded text-sm hover:bg-accent/30 disabled:opacity-50"
                            style={{ borderRadius: "var(--radius)", color: "var(--destructive)" }}
                            disabled={isSaving}
                            onClick={() => setConfirmDelete(selectedRole)}
                          >
                            Apagar
                          </button>
                        </>
                      )}
                    </div>

                    {!selectedRole.canEditFeatures && (
                      <p className="text-sm text-secondary-text">
                        O Administrador tem sempre acesso total a toda a aplicação — este cargo não se edita.
                      </p>
                    )}

                    {selectedRole.canEditFeatures && (vm?.features?.length ?? 0) > 0 && (
                      <button
                        type="button"
                        className="text-sm text-primary underline disabled:opacity-50"
                        disabled={isSaving}
                        onClick={toggleAllFeatures}
                      >
                        {checkedFeatures.size === (vm?.features?.length ?? 0)
                          ? "Desmarcar todas"
                          : "Selecionar todas"}
                      </button>
                    )}

                    <div
                      role="group"
                      aria-labelledby="features-heading"
                      className="border border-border bg-card p-4 grid grid-cols-1 md:grid-cols-2 gap-2"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {sortedFeatures.map((feature) => (
                        <label
                          key={feature.code}
                          className="flex items-center gap-2 text-sm text-foreground min-h-[44px] px-2"
                          style={{ borderRadius: "var(--radius)", cursor: selectedRole.canEditFeatures ? "pointer" : "default" }}
                        >
                          <input
                            type="checkbox"
                            checked={checkedFeatures.has(feature.code ?? "")}
                            disabled={!selectedRole.canEditFeatures || isSaving}
                            onChange={() => toggleFeature(feature.code ?? "")}
                          />
                          {feature.name}
                        </label>
                      ))}
                    </div>

                    {selectedRole.canEditFeatures && (
                      <button
                        type="button"
                        className="px-4 py-2 text-primary-foreground disabled:opacity-50"
                        style={{
                          borderRadius: "var(--radius)",
                          backgroundColor: "var(--primary)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                        disabled={isSaving}
                        aria-busy={isSaving}
                        onClick={saveFeatures}
                      >
                        {isSaving ? "A guardar…" : "Guardar funcionalidades"}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-secondary-text">Escolha um cargo à esquerda.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <Modal
          title="Apagar cargo"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button
                type="button"
                className="px-4 py-2 border border-border rounded text-foreground hover:bg-accent/30"
                style={{ borderRadius: "var(--radius)" }}
                onClick={() => setConfirmDelete(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-4 py-2 text-primary-foreground disabled:opacity-50"
                style={{ borderRadius: "var(--radius)", backgroundColor: "var(--destructive)" }}
                disabled={isSaving}
                onClick={deleteRole}
              >
                {isSaving ? "A apagar…" : "Apagar cargo"}
              </button>
            </>
          }
        >
          <p className="text-sm text-foreground">
            Apagar «{roleDisplayName(confirmDelete.name ?? "")}»?{" "}
            {confirmDelete.userCount
              ? `${confirmDelete.userCount} utilizador(es) têm este cargo e ficarão apenas com os cargos restantes.`
              : "Nenhum utilizador tem este cargo."}
          </p>
        </Modal>
      )}
    </div>
  );
}
